'use client';

import Link from 'next/link';
import { useSession, signIn } from 'next-auth/react';
import { User, Menu, X, Palette, BookOpen, Library, GraduationCap, Mail, Gem } from 'lucide-react';
import { useState, useEffect } from 'react';
import Image from 'next/image';
import { useRouter, usePathname } from 'next/navigation';
import { AccountSettingsModal } from './account-settings-modal';

export function Navbar() {
  const { data: session } = useSession();
  const router = useRouter();
  const pathname = usePathname();
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [credits, setCredits] = useState(0);
  const [plan, setPlan] = useState<'NONE' | 'AMAYA_PLUS' | 'AMAYA_PRO'>('NONE');

  // Fetch credits and subscription status
  const fetchCredits = async () => {
    if (session?.user) {
      try {
        const res = await fetch('/api/account');
        const data = await res.json();
        setCredits(data.credits || 0);
        setPlan(data.subscriptionPlan || 'NONE');
      } catch (err) {
        console.error('Error fetching account data:', err);
      }
    }
  };

  useEffect(() => {
    fetchCredits();
  }, [session]);

  // Listen for credit changes from other components
  useEffect(() => {
    const handleCreditsChanged = () => {
      console.log('Credits changed event received, refreshing...');
      fetchCredits();
    };

    window.addEventListener('creditsChanged', handleCreditsChanged);
    return () => window.removeEventListener('creditsChanged', handleCreditsChanged);
  }, [session]);

  // Handle hash navigation on page load
  useEffect(() => {
    if (pathname === '/' && window.location.hash) {
      const hash = window.location.hash.substring(1);
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          const navbarHeight = 80;
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - navbarHeight;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [pathname]);

  function scrollToSection(sectionId: string) {
    // If not on homepage, navigate there first
    if (pathname !== '/') {
      router.push('/#' + sectionId);
      return;
    }
    
    // Scroll to section with offset for sticky navbar
    const element = document.getElementById(sectionId);
    if (element) {
      const navbarHeight = 80; // Approximate navbar height
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - navbarHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <nav style={{
      position: 'sticky',
      top: 0,
      zIndex: 100,
      background: 'var(--color-bg-primary)',
      borderBottom: '1px solid var(--color-border)',
      boxShadow: 'var(--shadow-sm)',
    }}>
      <div className="navbar-container" style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '0.75rem 1rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        {/* Logo */}
        <Link href="/" className="navbar-logo" style={{ 
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          color: 'var(--color-warm-gray)',
          fontWeight: 700,
          fontSize: '1.25rem',
          flexShrink: 0,
        }}>
          {/* Desktop Logo */}
          <Image 
            src="/logo.png" 
            alt="Amaya Stories" 
            width={400} 
            height={84}
            className="navbar-logo-desktop"
            style={{ height: '42px', width: 'auto' }}
            priority
          />
          {/* Mobile Logo */}
          <Image 
            src="/logo-mob.png" 
            alt="Amaya Stories" 
            width={320} 
            height={67}
            className="navbar-logo-mobile"
            style={{ height: '40px', width: 'auto', display: 'none' }}
            priority
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="navbar-links" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '1.5rem'
        }}>
          <Link
            href="/coloring"
            className="nav-link"
            title="Coloring Books"
            style={{ 
              textDecoration: 'none',
              color: 'var(--color-text-primary)',
              fontWeight: 600,
              fontSize: '0.9375rem',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap',
              padding: '0.5rem 0.75rem',
              borderRadius: '12px',
              minWidth: '44px',
              minHeight: '44px',
              justifyContent: 'center',
            }}
          >
            <Palette size={20} strokeWidth={2.5} style={{ color: '#A8D8E6', flexShrink: 0 }} />
            <span className="nav-text">Coloring</span>
          </Link>
          
          <Link
            href="/audio-book/create"
            className="nav-link"
            title="Audio Picture Books"
            style={{ 
              textDecoration: 'none',
              color: 'var(--color-text-primary)',
              fontWeight: 600,
              fontSize: '0.9375rem',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap',
              padding: '0.5rem 0.75rem',
              borderRadius: '12px',
              minWidth: '44px',
              minHeight: '44px',
              justifyContent: 'center',
            }}
          >
            <BookOpen size={20} strokeWidth={2.5} style={{ color: '#C9B6E4', flexShrink: 0 }} />
            <span className="nav-text">Audio Picture Books</span>
          </Link>

          <Link
            href="/audio-book/library"
            className="nav-link"
            title="My Library"
            style={{ 
              textDecoration: 'none',
              color: 'var(--color-text-primary)',
              fontWeight: 600,
              fontSize: '0.9375rem',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap',
              padding: '0.5rem 0.75rem',
              borderRadius: '12px',
              minWidth: '44px',
              minHeight: '44px',
              justifyContent: 'center',
            }}
          >
            <Library size={20} strokeWidth={2.5} style={{ color: '#667eea', flexShrink: 0 }} />
            <span className="nav-text">Library</span>
          </Link>

          <Link
            href="/learning"
            className="nav-link"
            title="Learning Mode"
            style={{ 
              textDecoration: 'none',
              color: 'var(--color-text-primary)',
              fontWeight: 600,
              fontSize: '0.9375rem',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap',
              padding: '0.5rem 0.75rem',
              borderRadius: '12px',
              minWidth: '44px',
              minHeight: '44px',
              justifyContent: 'center',
            }}
          >
            <GraduationCap size={20} strokeWidth={2.5} style={{ color: '#10B981', flexShrink: 0 }} />
            <span className="nav-text">Learning</span>
          </Link>

          {session ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
            }}>
              {/* Credits Badge - Glassmorphic Design */}
              <Link
                href="/pricing"
                style={{
                  textDecoration: 'none',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15), rgba(255, 193, 7, 0.15))',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: '2px solid rgba(255, 215, 0, 0.3)',
                  borderRadius: '50px',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 4px 20px rgba(255, 215, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  color: '#D97706',
                  overflow: 'hidden',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(255, 215, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.borderColor = 'rgba(255, 215, 0, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(255, 215, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.borderColor = 'rgba(255, 215, 0, 0.3)';
                }}
              >
                {/* Animated gradient background */}
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1), rgba(255, 193, 7, 0.2), rgba(255, 215, 0, 0.1))',
                  backgroundSize: '200% 200%',
                  animation: 'gradientShift 3s ease infinite',
                  opacity: 0.5,
                  pointerEvents: 'none',
                }} />
                
                {/* Coin Icon */}
                <div style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '24px',
                  height: '24px',
                  background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                  borderRadius: '50%',
                  boxShadow: '0 2px 8px rgba(255, 215, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
                  animation: 'coinSpin 4s ease-in-out infinite',
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </div>
                
                {/* Credits Count */}
                <span style={{
                  position: 'relative',
                  background: 'linear-gradient(135deg, #D97706, #B45309)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                }}>
                  {credits}
                </span>
                
                {/* "Credits" label */}
                <span style={{
                  position: 'relative',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: '#92400E',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>
                  Credits
                </span>
              </Link>

              {/* Subscription Badge - Only show if subscribed */}
              {(plan === 'AMAYA_PLUS' || plan === 'AMAYA_PRO') && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  padding: '0.4rem 0.875rem',
                  background: plan === 'AMAYA_PRO'
                    ? 'linear-gradient(135deg, rgba(139, 69, 255, 0.15), rgba(165, 105, 255, 0.15))'
                    : 'linear-gradient(135deg, rgba(255, 215, 0, 0.15), rgba(255, 193, 7, 0.15))',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: plan === 'AMAYA_PRO'
                    ? '2px solid rgba(139, 69, 255, 0.4)'
                    : '2px solid rgba(255, 215, 0, 0.4)',
                  borderRadius: '50px',
                  boxShadow: plan === 'AMAYA_PRO'
                    ? '0 4px 20px rgba(139, 69, 255, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                    : '0 4px 20px rgba(255, 215, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                  animation: 'subtleGlow 3s ease-in-out infinite',
                }}>
                  {/* Crown/Star Icon */}
                  {plan === 'AMAYA_PRO' ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#8B45FF" stroke="#8B45FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2 8h20l-2 10H4L2 8z"/>
                      <path d="M6 8V6a6 6 0 0112 0v2"/>
                      <circle cx="12" cy="12" r="1" fill="white"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#FFD700" stroke="#FFD700" strokeWidth="2">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                  )}
                  
                  {/* Plan Name */}
                  <span style={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: plan === 'AMAYA_PRO' ? '#8B45FF' : '#D97706',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}>
                    {plan === 'AMAYA_PRO' ? 'Pro' : 'Plus'}
                  </span>
                </div>
              )}

              {/* User Profile Button */}
              <button
                onClick={() => setShowAccountModal(true)}
                style={{
                  background: 'linear-gradient(135deg, rgba(168, 216, 230, 0.2) 0%, rgba(201, 182, 228, 0.2) 100%)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: '2px solid rgba(168, 216, 230, 0.4)',
                  borderRadius: '50px',
                  padding: '0.5rem 1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.9375rem',
                  color: 'var(--color-text-primary)',
                  boxShadow: '0 4px 20px rgba(168, 216, 230, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(168, 216, 230, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.borderColor = 'rgba(168, 216, 230, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(168, 216, 230, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.borderColor = 'rgba(168, 216, 230, 0.4)';
                }}
              >
                {/* Shimmer effect */}
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                  transform: 'translateX(-100%)',
                  animation: 'shimmer 3s infinite',
                  pointerEvents: 'none',
                }} />
                
                <User size={18} style={{ position: 'relative' }} />
                <span style={{ position: 'relative' }}>
                  {session.user?.name || session.user?.email?.split('@')[0] || 'Account'}
                </span>
              </button>
              
              <AccountSettingsModal 
                isOpen={showAccountModal} 
                onClose={() => setShowAccountModal(false)} 
              />
            </div>
          ) : (
            <Link href="/login">
              <button
                className="btn-secondary"
                style={{ padding: '0.5rem 1.25rem' }}
              >
                Sign In
              </button>
            </Link>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="mobile-menu-button"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          style={{
            display: 'none',
            alignItems: 'center',
            justifyContent: 'center',
            width: '44px',
            height: '44px',
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
            border: '2px solid rgba(102, 126, 234, 0.3)',
            borderRadius: '12px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            backdropFilter: 'blur(10px)',
          }}
          aria-label="Toggle mobile menu"
        >
          {mobileMenuOpen ? <X size={24} color="#667eea" /> : <Menu size={24} color="#667eea" />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="mobile-menu-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(8px)',
            zIndex: 998,
            animation: 'fadeIn 0.3s ease',
          }}
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Panel */}
      <div
        className="mobile-menu-panel"
        style={{
          position: 'fixed',
          top: 0,
          right: mobileMenuOpen ? 0 : '-100%',
          width: '85%',
          maxWidth: '400px',
          height: '100vh',
          background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
          boxShadow: mobileMenuOpen ? '-4px 0 20px rgba(0, 0, 0, 0.15)' : 'none',
          zIndex: 999,
          transition: 'right 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '2rem 1.5rem',
          paddingBottom: 'max(2rem, env(safe-area-inset-bottom))',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Close Button */}
        <button
          onClick={() => setMobileMenuOpen(false)}
          style={{
            alignSelf: 'flex-end',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(102, 126, 234, 0.1)',
            border: '2px solid rgba(102, 126, 234, 0.2)',
            borderRadius: '12px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          aria-label="Close menu"
        >
          <X size={20} color="#667eea" />
        </button>

        {/* User Info Section */}
        {session ? (
          <div style={{
            padding: '1.5rem',
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
            borderRadius: '16px',
            border: '2px solid rgba(102, 126, 234, 0.15)',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              paddingBottom: '0.75rem',
              borderBottom: '1px solid rgba(102, 126, 234, 0.15)',
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontWeight: 700,
                fontSize: '1.25rem',
              }}>
                {session.user?.name?.charAt(0) || session.user?.email?.charAt(0) || 'A'}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1f2937' }}>
                  {session.user?.name || session.user?.email?.split('@')[0] || 'User'}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  {session.user?.email}
                </div>
              </div>
            </div>
            
            {/* Credits & Plan */}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <div style={{
                flex: 1,
                padding: '0.75rem',
                background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1), rgba(255, 193, 7, 0.1))',
                border: '2px solid rgba(255, 215, 0, 0.3)',
                borderRadius: '12px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#D97706' }}>
                  {credits}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#92400e', fontWeight: 600 }}>
                  Credits
                </div>
              </div>
              <div style={{
                flex: 1,
                padding: '0.75rem',
                background: plan === 'AMAYA_PRO' 
                  ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)'
                  : 'linear-gradient(135deg, rgba(168, 216, 230, 0.15) 0%, rgba(201, 182, 228, 0.15) 100%)',
                border: `2px solid ${plan === 'AMAYA_PRO' ? 'rgba(102, 126, 234, 0.4)' : 'rgba(168, 216, 230, 0.4)'}`,
                borderRadius: '12px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 700, color: plan === 'AMAYA_PRO' ? '#667eea' : '#4f46e5' }}>
                  {plan === 'AMAYA_PRO' ? 'PRO' : plan === 'AMAYA_PLUS' ? 'PLUS' : 'FREE'}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 600 }}>
                  Plan
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Navigation Links */}
        <nav style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
        }}>
          <Link
            href="/coloring"
            style={{
              textDecoration: 'none',
              padding: '1rem 1.25rem',
              background: pathname === '/coloring' 
                ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                : 'transparent',
              border: pathname === '/coloring' 
                ? '2px solid rgba(102, 126, 234, 0.3)'
                : '2px solid transparent',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              fontSize: '1rem',
              fontWeight: 600,
              color: '#1f2937',
              transition: 'all 0.2s ease',
            }}
          >
            <Palette size={22} strokeWidth={2.5} style={{ color: '#A8D8E6' }} />
            <span>Coloring Books</span>
          </Link>

          <Link
            href="/audio-book/create"
            style={{
              textDecoration: 'none',
              padding: '1rem 1.25rem',
              background: pathname === '/audio-book/create' 
                ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                : 'transparent',
              border: pathname === '/audio-book/create' 
                ? '2px solid rgba(102, 126, 234, 0.3)'
                : '2px solid transparent',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              fontSize: '1rem',
              fontWeight: 600,
              color: '#1f2937',
              transition: 'all 0.2s ease',
            }}
          >
            <BookOpen size={22} strokeWidth={2.5} style={{ color: '#C9B6E4' }} />
            <span>Create Audio Picture Book</span>
          </Link>

          <Link
            href="/audio-book/library"
            style={{
              textDecoration: 'none',
              padding: '1rem 1.25rem',
              background: pathname === '/audio-book/library' 
                ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                : 'transparent',
              border: pathname === '/audio-book/library' 
                ? '2px solid rgba(102, 126, 234, 0.3)'
                : '2px solid transparent',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              fontSize: '1rem',
              fontWeight: 600,
              color: '#1f2937',
              transition: 'all 0.2s ease',
            }}
          >
            <Library size={22} strokeWidth={2.5} style={{ color: '#667eea' }} />
            <span>My Library</span>
          </Link>

          <Link
            href="/learning"
            style={{
              textDecoration: 'none',
              padding: '1rem 1.25rem',
              background: pathname === '/learning' 
                ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                : 'transparent',
              border: pathname === '/learning' 
                ? '2px solid rgba(102, 126, 234, 0.3)'
                : '2px solid transparent',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              fontSize: '1rem',
              fontWeight: 600,
              color: '#1f2937',
              transition: 'all 0.2s ease',
            }}
          >
            <GraduationCap size={22} strokeWidth={2.5} style={{ color: '#10B981' }} />
            <span>Learning Mode</span>
          </Link>

          <Link
            href="/pricing"
            style={{
              textDecoration: 'none',
              padding: '1rem 1.25rem',
              background: pathname === '/pricing' 
                ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.15), rgba(255, 193, 7, 0.15))'
                : 'transparent',
              border: pathname === '/pricing' 
                ? '2px solid rgba(255, 215, 0, 0.4)'
                : '2px solid transparent',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              fontSize: '1rem',
              fontWeight: 600,
              color: '#1f2937',
              transition: 'all 0.2s ease',
            }}
          >
            <Gem size={22} strokeWidth={2.5} style={{ color: '#F59E0B' }} />
            <span>Pricing & Credits</span>
          </Link>

          <Link
            href="/contact"
            style={{
              textDecoration: 'none',
              padding: '1rem 1.25rem',
              background: pathname === '/contact' 
                ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                : 'transparent',
              border: pathname === '/contact' 
                ? '2px solid rgba(102, 126, 234, 0.3)'
                : '2px solid transparent',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              fontSize: '1rem',
              fontWeight: 600,
              color: '#1f2937',
              transition: 'all 0.2s ease',
            }}
          >
            <Mail size={22} strokeWidth={2.5} style={{ color: '#F8C8A0' }} />
            <span>Contact Us</span>
          </Link>
        </nav>

        {/* Action Buttons - Positioned for iPhone visibility */}
        <div style={{
          marginTop: '1.5rem',
          paddingTop: '1.5rem',
          paddingBottom: '2rem',
          borderTop: '2px solid rgba(102, 126, 234, 0.1)',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
        }}>
          {session ? (
            <>
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  setShowAccountModal(true);
                }}
                style={{
                  padding: '1rem',
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                  border: '2px solid rgba(102, 126, 234, 0.3)',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: '#667eea',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                }}
              >
                <User size={20} />
                Account Settings
              </button>
            </>
          ) : (
            <Link href="/login" onClick={() => setMobileMenuOpen(false)}>
              <button
                style={{
                  width: '100%',
                  padding: '1.125rem',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '1.0625rem',
                  fontWeight: 700,
                  color: '#fff',
                  cursor: 'pointer',
                  boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
                  transition: 'all 0.2s ease',
                }}
              >
                Sign In
              </button>
            </Link>
          )}
        </div>
      </div>

      <style jsx global>{`
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes coinSpin {
          0%, 100% { transform: rotateY(0deg); }
          50% { transform: rotateY(180deg); }
        }
        
        @keyframes subtleGlow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        /* Mobile Menu Animations */
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        /* Nav Link Hover Effects */
        .nav-link:hover {
          background: linear-gradient(135deg, rgba(168, 216, 230, 0.12) 0%, rgba(201, 182, 228, 0.12) 100%);
          transform: translateY(-2px);
        }
        
        .nav-link:active {
          transform: translateY(0);
        }
        
        /* Touch devices - larger tap targets */
        @media (hover: none) and (pointer: coarse) {
          .nav-link {
            min-width: 48px !important;
            min-height: 48px !important;
            padding: 0.625rem 0.875rem !important;
          }
          .mobile-menu-button {
            min-width: 48px !important;
            min-height: 48px !important;
          }
        }

        /* Mobile - Phones and small tablets (portrait) */
        @media (max-width: 900px) {
          /* Show mobile logo, hide desktop logo */
          .navbar-logo-desktop {
            display: none !important;
          }
          .navbar-logo-mobile {
            display: block !important;
            height: 38px !important;
            width: auto !important;
          }
          
          /* Reduce navbar padding */
          .navbar-container {
            padding: 0.625rem 1rem !important;
          }
          
          /* Hide desktop navigation on mobile */
          .navbar-links {
            display: none !important;
          }
          
          /* Show mobile menu button */
          .mobile-menu-button {
            display: flex !important;
          }
          
          /* Mobile menu panel - ensure scrollable content fits */
          .mobile-menu-panel {
            /* Use dynamic viewport height for better iPhone support */
            height: 100dvh !important;
            max-height: -webkit-fill-available !important;
          }
        }
        
        /* iPhone specific - handle Safari bottom bar */
        @supports (-webkit-touch-callout: none) {
          @media (max-width: 900px) {
            .mobile-menu-panel {
              /* Extra padding for iPhone bottom bar */
              padding-bottom: calc(2rem + env(safe-area-inset-bottom, 60px)) !important;
            }
          }
        }
        
        /* Larger phones and small tablets */
        @media (min-width: 600px) and (max-width: 900px) {
          .navbar-logo-mobile {
            height: 42px !important;
          }
          .navbar-container {
            padding: 0.75rem 1.25rem !important;
          }
        }
        
        /* Medium Desktop - Compact nav items */
        @media (min-width: 901px) and (max-width: 1200px) {
          /* Show desktop logo, hide mobile logo */
          .navbar-logo-desktop {
            display: block !important;
            height: 38px !important;
            width: auto !important;
          }
          .navbar-logo-mobile {
            display: none !important;
          }
          
          /* Reduce spacing between nav items */
          .navbar-links {
            gap: 0.75rem !important;
          }
          
          /* Hide text on nav links, show only icons */
          .nav-text {
            display: none !important;
          }
          
          /* Make nav links more compact */
          .nav-link {
            padding: 0.5rem 0.625rem !important;
            font-size: 0.875rem !important;
          }
          
          /* Reduce navbar padding */
          .navbar-container {
            padding: 0.625rem 1rem !important;
          }
          
          /* Make credits badge more compact */
          .navbar-links a[href="/pricing"] {
            padding: 0.4rem 0.75rem !important;
            font-size: 0.8125rem !important;
          }
          
          /* Make subscription badge more compact */
          .navbar-links > div > div:last-child {
            padding: 0.35rem 0.65rem !important;
            font-size: 0.6875rem !important;
          }
          
          /* Make user button more compact */
          .navbar-links button {
            padding: 0.45rem 0.875rem !important;
            font-size: 0.875rem !important;
          }
        }
        
        /* Large Desktop - Full nav */
        @media (min-width: 1201px) {
          /* Show desktop logo, hide mobile logo */
          .navbar-logo-desktop {
            display: block !important;
            height: 42px !important;
            width: auto !important;
          }
          .navbar-logo-mobile {
            display: none !important;
          }
          
          /* Full spacing */
          .navbar-links {
            gap: 1.5rem !important;
          }
          
          .nav-text {
            display: inline !important;
          }
        }
        
        /* Extra large screens - even more spacing */
        @media (min-width: 1400px) {
          .navbar-logo-desktop {
            height: 46px !important;
          }
          .navbar-container {
            padding: 0.875rem 1.5rem !important;
          }
        }
      `}</style>
    </nav>
  );
}

